import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import type { Case } from "../api/types.ts"
import { useT } from "../i18n/index.tsx"
import { Drawer } from "../ui/index.ts"
import { caseTitle } from "./case-columns.tsx"
import { CaseDetailView } from "./case-detail/CaseDetailView.tsx"

/** 제목을 바로 그리는 데 필요한 만큼만. 목록·스캔·선적이 이미 들고 있는 행이다. */
type TitleRow = Pick<Case, "id" | "seq" | "orderNo">

/**
 * 반품 상세 옆판. 목록·스캔·선적 화면이 모두 이 하나를 쓴다.
 *
 * 열린 건은 `?detail=<id>` 로 URL 에 남는다 — 새로고침·뒤로가기·링크 공유가 그대로 되고,
 * 어느 화면에서 열었든 같은 방식으로 동작한다.
 */
export function useCaseDetailPanel(
  options: {
    /** 옆판에서 상태가 바뀌었을 때. 보통 뒤 목록을 다시 읽는다. */
    onChanged?: () => void
    /**
     * 화면이 이미 들고 있는 행들. 여기서 제목을 만들므로 깜빡임 없이 바로 보인다
     * (없으면 "반품 상세"만 띄운다 — 내용은 옆판이 스스로 읽는다).
     */
    rows?: readonly TitleRow[]
  } = {},
) {
  const t = useT()
  const [params, setParams] = useSearchParams()
  const openId = params.get("detail")
  const { onChanged, rows } = options

  const setDetail = useCallback(
    (id: number | null) => {
      setParams((current) => {
        const next = new URLSearchParams(current)
        if (id === null) next.delete("detail")
        else next.set("detail", String(id))
        return next
      })
    },
    [setParams],
  )

  const open = useCallback((id: number) => setDetail(id), [setDetail])
  const close = useCallback(() => setDetail(null), [setDetail])

  const row = openId === null ? undefined : rows?.find((item) => item.id === Number(openId))

  const panel =
    openId === null ? null : (
      <Drawer title={row ? caseTitle(t, row) : t("caseDetail.title")} onClose={close}>
        <CaseDetailView caseId={Number(openId)} showHeading={false} onChanged={onChanged} />
      </Drawer>
    )

  return { open, close, panel, openId }
}
